import { QueryClient, type QueryKey } from '@tanstack/react-query';
import { useRef, useSyncExternalStore } from 'react';

// The coordination layer behind StoreContext.tsx's syncChannels/
// syncTemplates/loadMessages (see their own comments) - a single shared
// QueryClient whose cache dedupes concurrent calls for the same key (two
// callers racing for the same channels page share one in-flight request
// instead of firing two), retries a transient failure with backoff
// instead of surfacing it immediately, and treats a just-fetched result
// as fresh for a short window so back-to-back triggers (e.g. Cue relay
// events firing loadMessages repeatedly while a channel is open - see
// subscribeToChannel) don't refetch from scratch every time.
//
// Used via queryClient.query(...) at each call site rather than the
// idiomatic useQuery(...) hook - StoreContext.tsx's channels/templates
// state (messages live nested inside each Channel, so migrating channels
// covers them too) is query-owned now (see useQueryState below), but still
// mutated directly by the same ~15 functions (addMessage, patchChannel,
// applyChannelStats, updateChannelViewType, ...) using useState's exact
// setter shape - query itself leaves those call sites completely alone,
// only the underlying network calls route through it.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10_000),
      staleTime: 5_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      // These reads are driven explicitly by StoreContext.tsx's own
      // callers (a sync button, a mount effect, a Cue relay), not by
      // component mount/remount - refetchOnMount would otherwise silently
      // ignore the staleTime window above the moment any component
      // re-renders.
      refetchOnMount: false,
    },
  },
});

// channelsState/templatesState/mappingsState (see queryKeys below and
// useQueryState further down) hold StoreContext.tsx's actual channels/
// templates/mappings arrays, not a network fetch result - nothing ever
// calls queryClient.query for these keys, so the default 5-minute gcTime
// would otherwise silently evict them (see Query#scheduleGc/optionalRemove
// in the installed source - a Query with zero QueryObserver-based
// observers, which these never get since they're read via
// useSyncExternalStore instead of useQuery, becomes GC-eligible gcTime
// after it's built) and StoreContext would wake up with `mappings`
// (or channels/templates) reading back as undefined mid-session -
// useQueryState's own seed-once-per-mount `seeded` ref never notices and
// never reseeds, so every later setMappings((prev) => prev.map(...)) call
// (updateMapping, rotateMappingIngestToken, ...) throws on `prev` being
// undefined instead of []. These three are the actual state store, not a
// cache of something re-fetchable - they must never expire on their own.
//
// mappingsState was the one of the three missing this when it was first
// added - confirmed live: editing a "system" mapping and clicking Save
// after the tab had been open (idle on that screen, so no mappings-touching
// query ran) past gcTime threw exactly this.
queryClient.setQueryDefaults(['channelsState'], { gcTime: Infinity });
queryClient.setQueryDefaults(['templatesState'], { gcTime: Infinity });
queryClient.setQueryDefaults(['mappingsState'], { gcTime: Infinity });

// Stable query key builders - every query call site should build its
// key through these rather than inlining array literals, so a typo can't
// silently create a second, undeduped cache entry for the same data.
export const queryKeys = {
  // Back StoreContext.tsx's channels/templates state itself (via
  // useQueryState below), not a network fetch page - deliberately a
  // SEPARATE top-level key namespace from channels(offset)/templates(offset)
  // below (not e.g. ['channels', 'list']), even though both are
  // conceptually "about channels": updateChannel/createChannel/
  // deleteChannel invalidateQueries({queryKey: ['channels']}) after their
  // mutation (a PREFIX match), which would otherwise also match a
  // ['channels', 'list']-shaped key and try to refetch/invalidate the
  // state cell itself.
  channelsState: () => ['channelsState'] as const,
  templatesState: () => ['templatesState'] as const,
  mappingsState: () => ['mappingsState'] as const,
  channels: (offset: number) => ['channels', offset] as const,
  templates: (offset: number) => ['templates', offset] as const,
  mappings: (offset: number) => ['mappings', offset] as const,
  channelMessages: (channelId: string, offset: number) => ['channelMessages', channelId, offset] as const,
  // Per-channel/template toggle actions - keyed by nickname AND the
  // specific action (not just nickname) so a rapid double-submit of the
  // SAME action (double click, or a click landing right after a keyboard
  // activation) dedupes, while an opposite-direction toggle arriving
  // before the first settles (like then quickly unlike) is a DIFFERENT
  // key and always runs for real - two query calls sharing a key
  // while one is in flight reuse that first call's queryFn, not the
  // second caller's, so collapsing "like" and "unlike" onto the same key
  // would risk the unlike button's click actually re-running (and
  // resolving to the result of) a like call still in flight.
  channelLike: (nickname: string, action: 'like' | 'unlike') => ['channelLike', nickname, action] as const,
  channelFollow: (nickname: string, action: 'follow' | 'unfollow') => ['channelFollow', nickname, action] as const,
  channelMute: (nickname: string, action: 'mute' | 'unmute') => ['channelMute', nickname, action] as const,
  templateLike: (nickname: string, action: 'like' | 'unlike') => ['templateLike', nickname, action] as const,
  // Deliberately NOT parametrized by password - a query key lives in the
  // client's in-memory cache for as long as gcTime, so a plaintext
  // password has no business becoming part of one. Two enable calls for
  // the SAME channel racing (e.g. the toggle and ensureShareLinkUrl's
  // auto-enable firing close together) just dedupe onto whichever's
  // queryFn ran first, the same acceptable toggle-collision behavior
  // channelLike/channelFollow above already have.
  shareLinkEnable: (nickname: string) => ['shareLinkEnable', nickname] as const,
  shareLinkDisable: (nickname: string) => ['shareLinkDisable', nickname] as const,
  // Same "not parametrized by password" reasoning as shareLinkEnable above.
  shareLinkPassword: (nickname: string) => ['shareLinkPassword', nickname] as const,
  shareLinkRequestToJoin: (nickname: string, enabled: boolean) => ['shareLinkRequestToJoin', nickname, enabled] as const,
  channelSearchable: (nickname: string, enabled: boolean) => ['channelSearchable', nickname, enabled] as const,
  channelMemberApprove: (nickname: string, userId: string) => ['channelMemberApprove', nickname, userId] as const,
  channelInviteAccept: (nickname: string) => ['channelInviteAccept', nickname] as const,
  inviteEmail: (nickname: string, email: string) => ['inviteEmail', nickname, email] as const,
  // Real, cacheable reads.
  channelMembers: (nickname: string) => ['channelMembers', nickname] as const,
  channelInvites: () => ['channelInvites'] as const,
  channelSearch: (query: string, offset: number) => ['channelSearch', query, offset] as const,
  templateSearch: (scope: string, query: string, offset: number) => ['templateSearch', scope, query, offset] as const,
  mappingSearch: (scope: string, query: string, offset: number) => ['mappingSearch', scope, query, offset] as const,
  userSearch: (term: string) => ['userSearch', term] as const,
  profile: (email: string) => ['profile', email] as const,
  security: () => ['security'] as const,
  // Pricing-tier catalog (public, rarely changes) and the caller's live
  // billing state (plan + usage) - see StoreContext.tsx's billing effect
  // and api/billingApi.ts.
  tierCatalog: () => ['tierCatalog'] as const,
  billing: () => ['billing'] as const,
};

// Gives a piece of state a shared, cache-backed home instead of a plain
// per-component-instance useState, while keeping useState's exact
// (value | updater) => void setter shape - every existing
// setChannels(...)/setTemplates(...) call site in StoreContext.tsx needed
// no changes to use this.
//
// Deliberately NOT built on useQuery(key) - see StoreContext.tsx's
// channels/templates comment for the full history: a first attempt at this
// migration read the value back via useQuery(key).data and broke 7 tests,
// because useQuery's own re-render subscription (useBaseQuery.ts wraps it
// in notifyManager.batchCalls) is deferred through notifyManager's
// scheduler - setTimeout(0) by default - unlike React's synchronous
// setState batching. A write (setQueryData) followed by a same-tick read
// of the hook's own return value could see stale data - reproduced with
// real timers, not a test artifact - so that attempt was reverted rather
// than guessed through.
//
// Subscribing directly to queryClient.getQueryCache().subscribe(...)
// instead sidesteps that, confirmed from the installed source: Query#dispatch
// synchronously updates state, then calls notifyManager.batch(() => {
// observers...; cache.notify(...) }) - batch() invokes its callback
// immediately; it's only notifyManager.schedule/batchCalls (the path
// useQuery's hook subscription specifically opts into via
// useBaseQuery.ts) that defers via setTimeout(0). Cache-level listeners
// therefore fire synchronously in the same call stack as the setQueryData
// that triggered them, giving useState-equivalent read-after-write
// semantics for one specific cache entry - without reconfiguring
// notifyManager's scheduler app-wide, the other option considered and a
// much bigger blast radius (every useQuery/useIsFetching re-render in the
// app would also stop being batched).
//
// See this file's setQueryDefaults calls above for why any key used with
// this hook needs gcTime: Infinity - this is the actual state, not a
// re-fetchable cache of it, and must never silently expire.
export function useQueryState<T>(
  queryKey: QueryKey,
  initialValue: T | (() => T)
): [T, (updater: T | ((prev: T) => T)) => void] {
  const seeded = useRef(false);
  if (!seeded.current) {
    if (queryClient.getQueryData<T>(queryKey) === undefined) {
      queryClient.setQueryData<T>(queryKey, typeof initialValue === 'function' ? (initialValue as () => T)() : initialValue);
    }
    seeded.current = true;
  }

  const value = useSyncExternalStore(
    (onStoreChange) => queryClient.getQueryCache().subscribe(() => onStoreChange()),
    () => queryClient.getQueryData<T>(queryKey) as T
  );

  const setValue = (updater: T | ((prev: T) => T)) => {
    queryClient.setQueryData<T>(queryKey, (prev) =>
      typeof updater === 'function' ? (updater as (p: T) => T)(prev as T) : updater
    );
  };

  return [value, setValue];
}
